import { colors } from '@lmc/common/constants'
import type { RouterProps } from '@lmc/common/types'
import { Text } from '@lmc/ui-kit/src/general'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { selfServeConstants } from 'locale/selfServe'
import { type FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { gaEvent } from '../../../common/services/analytics'
import { getJSON } from '../../../common/services/api'
import { getNominative } from '../../../common/utils'
import {
  DashboardListItem,
  ProgressRing,
  Spinner
} from '../../../components/generic'
import { ResponsiveText } from '../../../LEGACY/elemental'
import { getProgressColor } from '../utils/progressColor'
import { EmptyItems, HomeCard } from './index'

type Props = {
  isLoadingProp: boolean
  router?: RouterProps
  homeID?: string
  hasPro?: boolean
}

export const CarePlansCard: FC<Props> = ({
  router,
  isLoadingProp,
  homeID,
  hasPro
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [percentage, setPercentage] = useState<number>()

  const { t } = useTranslation(NAMESPACE.SELF_SERVE)

  const [carePlanCounts, setCarePlanCounts] = useState<{
    thisWeek: string
    overdue: string
    today: string
  }>()

  const hasMounted = useRef(false)

  const carePlanNominative = useSelector(
    (state: { global: { home: { preferredCarePlansNominative: string } } }) =>
      state.global.home.preferredCarePlansNominative
  )

  const fetchPercentage = async () => {
    try {
      const percentage = await getJSON({
        url: '/api/list/care-plans?percentageComplete=true'
      })
      const utcOffset = dayjs().utcOffset()
      const carePlanCounts = await getJSON({
        url: `/api/aggregations/care-plans?utcOffset=${utcOffset}`
      })

      setPercentage(percentage.result)
      if (carePlanCounts.result) {
        setCarePlanCounts({
          thisWeek: carePlanCounts.result.week,
          overdue: carePlanCounts.result.overdue,
          today: carePlanCounts.result.today
        })
      }
      setIsLoading(false)
    } catch (e) {
      setIsLoading(false)
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    fetchPercentage()
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true
      return
    }
    fetchPercentage()
  }, [homeID])

  const redirect = () => {
    const url = carePlanCounts
      ? '/reports/care-planning/care-plans'
      : '/residents?carePlans=true'
    router?.push(url)
  }

  const triggerGAEvent = (action: string) =>
    gaEvent({
      category: 'home-dashboard',
      action,
      label: undefined
    })

  const preferredCarePlanNameButton = getNominative(
    carePlanNominative,
    'care plan'
  )?.toLocaleLowerCase()

  const renderEmptyMessage = () => (
    <EmptyStateWrapper>
      <EmptyItems items={3} />
      {hasPro && (
        <InfoWrapper>
          <StyledText>
            {t(selfServeConstants.SEE_WHAT, { variable: carePlanNominative })}
          </StyledText>
          <div onClick={redirect}>
            <StyledTextHere>
              {t(selfServeConstants.VIEW_EXAMPLE, {
                variable: preferredCarePlanNameButton
              })}
            </StyledTextHere>
          </div>
          <HereWrapper />
        </InfoWrapper>
      )}
    </EmptyStateWrapper>
  )

  const renderBody = () => {
    if (!carePlanCounts || !percentage) {
      return renderEmptyMessage()
    }

    const { today, thisWeek, overdue } = carePlanCounts
    return (
      <div onClick={redirect} style={styles.notifications}>
        <DashboardListItem
          iconName="overdue"
          iconFill={colors.red}
          text="due today"
          count={today}
        />
        <DashboardListItem
          iconName="clock"
          iconFill={colors.orange}
          text="due this week"
          count={thisWeek}
        />
        <DashboardListItem
          iconName="overdue"
          iconFill={colors.red}
          text="overdue"
          count={overdue}
        />
      </div>
    )
  }

  const renderFooter = () => {
    if (percentage === 0) return null

    return (
      <div style={styles.footerContainer}>
        <div style={styles.progressContainer}>
          <ProgressRing
            color={getProgressColor(percentage)}
            radius={45}
            progress={percentage}
            style={styles.progressRing}
          />
          <ResponsiveTextStyled
            visibleSM="completed"
            visibleMD="of all completed"
            visibleLG={`of all ${getNominative(
              carePlanNominative,
              'Care Plans'
            )} completed`}
          />
        </div>
      </div>
    )
  }

  if (!router) return null

  const isLoadingAll = isLoading || isLoadingProp

  return (
    <HomeCard
      title={getNominative(carePlanNominative, 'Care Plans')}
      onTitleClick={() => {
        triggerGAEvent('Click care_plans_title_link')
        redirect()
      }}
      footer={!isLoadingAll ? renderFooter() : undefined}
    >
      {isLoadingAll ? <Spinner style={styles.spinner} /> : renderBody()}
    </HomeCard>
  )
}

const styles = {
  buttonText: {
    color: colors.black,
    opacity: 0.6
  },
  emptyHeader: {
    fontSize: 16,
    fontWeight: 'bold'
  },
  emptyLink: {
    margin: '0px 5px'
  },
  emptyMessage: {
    paddingTop: 20
  },
  footerContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between'
  },
  link: {
    cursor: 'pointer'
  },
  notifications: {
    cursor: 'pointer'
  },
  progressContainer: {
    alignItems: 'center',
    color: colors.darkgrey,
    display: 'flex'
  },
  progressRing: {
    marginRight: 8
  },
  spinner: {
    paddingTop: 30
  }
}

const InfoWrapper = styled.div`
  background-color: #fff8fa;
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
`

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const HereWrapper = styled.div`
  display: flex;
`

const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-top: 0;
  margin-bottom: auto;
`

const StyledTextHere = styled(Text)`
  color: ${({ theme }) => theme.color.status.popout};
  text-decoration: underline;
  cursor: pointer;
`

const ResponsiveTextStyled = styled(ResponsiveText)`
  color: ${(props) => props.theme.color.neutral.color4};
  font-weight: ${(props) => props.theme.font.weight.medium};
  font-size: 1rem;
`

export default CarePlansCard
